'use client';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Progress, Stack } from '@mantine/core';
import { PostResponse } from '@/queries/posts/usePost';
import { getTaskBlockState } from '@/queries/posts/utils';
import { InputSection } from '../../PostPage/InputSection';
import { PostChecklistAddItem } from './PostChecklistAddItem';
import { PostChecklistItem } from './PostChecklistItem';

export function PostChecklist({ post }: { post: PostResponse }) {
  const { t } = useTranslation();

  const progress =
    ((post.postTasks?.filter((item) => item.completedAt).length ?? 0) /
      (post.postTasks?.length ?? 1)) *
    100;

  return (
    <InputSection title={t('posts.checklist')} iconName="checkbox">
      <Progress size="xs" value={progress} />

      <Stack gap={0}>
        {post.postTasks
          ?.sort((a, b) => {
            // Check if tasks are blocked
            const aIsBlocked = getTaskBlockState(a, post.postTasks, post.post).isBlocked;
            const bIsBlocked = getTaskBlockState(b, post.postTasks, post.post).isBlocked;

            // Check if tasks are completed
            const aIsCompleted = !!a.completedAt;
            const bIsCompleted = !!b.completedAt;

            // Show blocked tasks at the bottom
            if (aIsBlocked && !bIsBlocked) {
              return 1;
            }

            if (!aIsBlocked && bIsBlocked) {
              return -1;
            }

            // Show completed tasks at the bottom (but above blocked tasks)
            if (aIsCompleted && !bIsCompleted) {
              return 1;
            }

            if (!aIsCompleted && bIsCompleted) {
              return -1;
            }

            // Completed tasks should be sorted by order
            if (aIsCompleted && bIsCompleted) {
              return (a.order ?? 0) - (b.order ?? 0);
            }

            if (!a.dueDate || !b.dueDate) {
              return (a.order ?? 0) - (b.order ?? 0);
            }

            // For non-blocked, non-completed tasks, sort by due date
            return dayjs.utc(a.dueDate).diff(dayjs.utc(b.dueDate));
          })
          .map((item) => (
            <PostChecklistItem key={item.id} task={item} tasks={post.postTasks} post={post.post} />
          ))}
      </Stack>
      <PostChecklistAddItem post={post} />
    </InputSection>
  );
}
